import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { doc, onSnapshot } from "firebase/firestore";
import { getFunctions, httpsCallable } from "firebase/functions";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  CalendarClock,
  CheckCircle2,
  Lock,
  Sparkles,
  Wallet,
  XCircle,
  Layers3,
  RefreshCw,
} from "lucide-react";
import { db } from "../firebase";
import { useAuth } from "../context/AuthContext";

const formatDate = (value) => {
  if (!value) return "—";
  const date =
    typeof value?.toDate === "function"
      ? value.toDate()
      : typeof value === "number"
      ? new Date(value > 9999999999 ? value : value * 1000)
      : new Date(value);

  if (Number.isNaN(date.getTime())) return "—";

  return new Intl.DateTimeFormat("en-IN", {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(date);
};

export default function MySubscriptionsPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const functions = getFunctions();
  const cancelSubscription = httpsCallable(functions, "cancelSubscription");

  const [loading, setLoading] = useState(true);
  const [userData, setUserData] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [selectedSubscription, setSelectedSubscription] = useState(null);

  useEffect(() => {
    if (!user?.uid) return;

    const unsubUser = onSnapshot(doc(db, "users", user.uid), (snap) => {
      setUserData(snap.exists() ? snap.data() : null);
      setLoading(false);
    });

    const unsubSub = onSnapshot(doc(db, "subscriptions", user.uid), (snap) => {
      setSubscription(snap.exists() ? snap.data() : null);
      setLoading(false);
    });

    return () => {
      unsubUser();
      unsubSub();
    };
  }, [user?.uid]);

  const handleCancelConfirm = async () => {
    try {
      setCancelling(true);
      await cancelSubscription();
      setShowCancelModal(false);
      setSelectedSubscription(null);
      alert("Recurring payment stopped. Your current plan stays active until the billing period ends.");
    } catch (error) {
      alert("Failed to update subscription right now.");
    } finally {
      setCancelling(false);
    }
  };

  const formatPlanType = (value) => {
    if (!value) return "learning roadmap";
    return String(value)
      .replace(/-/g, " ")
      .replace(/\b\w/g, (char) => char.toUpperCase());
  };

  const rawSubscriptionItems = Array.isArray(subscription?.subscriptions)
    ? subscription.subscriptions
    : Array.isArray(subscription?.items)
    ? subscription.items
    : subscription
    ? [subscription]
    : [];

  const normalizedItems = rawSubscriptionItems
    .map((item, index) => {
      const title =
        item?.planTitle ||
        item?.plan ||
        (index === 0 ? userData?.activePlanTitle : "") ||
        "";
      const type =
        item?.planType ||
        item?.productLine ||
        (index === 0 ? userData?.activePlanType : "") ||
        "learning-roadmap";
      const monthlyPrice =
        Number(item?.price?.monthly || 0) || Number(item?.monthlyPrice || 0);
      const status =
        item?.status ||
        (index === 0 && userData?.plan === "PRO" ? "active" : "free");
      const autoRenew =
        typeof item?.autoRenew === "boolean"
          ? item.autoRenew
          : index === 0 && typeof userData?.autoRenew === "boolean"
          ? userData.autoRenew
          : null;

      if (!title && status === "free") return null;

      return {
        id:
          item?.razorpaySubId ||
          item?.planId ||
          `${type}-${title || "subscription"}-${index}`,
        title: title || "FREE",
        type,
        monthlyPrice,
        status,
        autoRenew,
        currentPeriodEnd: item?.currentPeriodEnd,
        razorpaySubId: item?.razorpaySubId || "",
        canManage: index === 0 && !!(item?.razorpaySubId || subscription?.razorpaySubId),
      };
    })
    .filter(Boolean);

  const subscriptionsList = normalizedItems.length
    ? normalizedItems
    : [
        {
          id: "free-default",
          title: "FREE",
          type: "learning-roadmap",
          monthlyPrice: 0,
          status: "free",
          autoRenew: null,
          currentPeriodEnd: null,
          razorpaySubId: "",
          canManage: false,
        },
      ];

  const activeCount = subscriptionsList.filter((item) =>
    ["active", "authenticated", "pending", "cancel_requested"].includes(
      String(item.status || "").toLowerCase()
    )
  ).length;

  const renewableCount = subscriptionsList.filter((item) => item.autoRenew === true).length;
  const manageableSubscription = subscriptionsList.find((item) => item.canManage);

  if (loading) {
    return (
      <Page>
        <Shell>
          <LoadingCard />
          <LoadingGrid>
            <LoadingBlock />
            <LoadingBlock />
          </LoadingGrid>
        </Shell>
      </Page>
    );
  }

  return (
    <Page>
      <Shell>
        <BackButton onClick={() => navigate("/profile")}>
          <ArrowLeft size={16} />
          Back to Profile
        </BackButton>

        <Hero>
          <div>
            <Eyebrow>
              <Sparkles size={14} />
              My Subscriptions
            </Eyebrow>
            <Title>See your current plan and billing state clearly</Title>
            <Sub>
              Review every subscription track from one place, so learning, product
              development, or interview plans never feel mixed up.
            </Sub>
          </div>

          <SummaryPanel>
            <SummaryLabel>Subscription overview</SummaryLabel>
            <SummaryTitle>{subscriptionsList.length} subscription{subscriptionsList.length > 1 ? "s" : ""}</SummaryTitle>
            <SummaryList>
              <SummaryItem>
                <Layers3 size={16} />
                {activeCount} active track{activeCount !== 1 ? "s" : ""}
              </SummaryItem>
              <SummaryItem>
                <RefreshCw size={16} />
                {renewableCount} auto-renew enabled
              </SummaryItem>
              <SummaryItem>
                <CheckCircle2 size={16} />
                {manageableSubscription?.title || "No paid plan yet"}
              </SummaryItem>
            </SummaryList>
          </SummaryPanel>
        </Hero>

        <StatsGrid>
          <StatCard>
            <StatLabel>
              <Layers3 size={14} />
              Total subscriptions
            </StatLabel>
            <StatValue>{subscriptionsList.length}</StatValue>
            <StatHint>Every subscription track listed in one clean place.</StatHint>
          </StatCard>

          <StatCard>
            <StatLabel>
              <Sparkles size={14} />
              Active now
            </StatLabel>
            <StatValue>{activeCount}</StatValue>
            <StatHint>Plans currently active for this account.</StatHint>
          </StatCard>

          <StatCard>
            <StatLabel>
              <Wallet size={14} />
              Auto renew on
            </StatLabel>
            <StatValue>{renewableCount}</StatValue>
            <StatHint>Subscriptions that will charge again next cycle.</StatHint>
          </StatCard>

          <StatCard>
            <StatLabel>
              <CalendarClock size={14} />
              Next period end
            </StatLabel>
            <StatValue>{formatDate(manageableSubscription?.currentPeriodEnd)}</StatValue>
            <StatHint>The closest active billing cutoff we can detect right now.</StatHint>
          </StatCard>
        </StatsGrid>

        <ContentGrid>
          <Section>
            <SectionTitle>Your Subscription List</SectionTitle>
            <SectionSub>
              Each card shows the purchased plan title, plan type, billing state,
              and renewal behavior for that subscription track.
            </SectionSub>

            <SubscriptionList>
              {subscriptionsList.map((item) => (
                <SubscriptionCard key={item.id}>
                  <SubscriptionTop>
                    <div>
                      <SubscriptionTitle>{item.title}</SubscriptionTitle>
                      <SubscriptionMeta>{formatPlanType(item.type)}</SubscriptionMeta>
                    </div>
                    <StatusBadge $status={String(item.status || "").toLowerCase()}>
                      {item.status}
                    </StatusBadge>
                  </SubscriptionTop>

                  <Rows>
                    <Row>
                      <Label>Price</Label>
                      <Value>
                        {item.monthlyPrice ? `₹${item.monthlyPrice}/month` : "Free"}
                      </Value>
                    </Row>
                    <Row>
                      <Label>Auto renew</Label>
                      <Value>
                        {item.autoRenew === null
                          ? "—"
                          : item.autoRenew
                          ? "Enabled"
                          : "Stopped"}
                      </Value>
                    </Row>
                    <Row>
                      <Label>Current period end</Label>
                      <Value>{formatDate(item.currentPeriodEnd)}</Value>
                    </Row>
                    <Row>
                      <Label>Subscription ID</Label>
                      <Value>{item.razorpaySubId || "—"}</Value>
                    </Row>
                  </Rows>

                  <CardActions>
                    <InlineButton onClick={() => navigate("/pricing")}>
                      Open Pricing
                    </InlineButton>
                    {item.canManage ? (
                      <InlineDangerButton
                        onClick={() => {
                          setSelectedSubscription(item);
                          setShowCancelModal(true);
                        }}
                      >
                        <XCircle size={15} />
                        Stop Recurring Payment
                      </InlineDangerButton>
                    ) : null}
                  </CardActions>
                </SubscriptionCard>
              ))}
            </SubscriptionList>
          </Section>

          <Section>
            <SectionTitle>Billing Help</SectionTitle>
            <SectionSub>
              Use the action inside the matching subscription card to stop its
              recurring payment. Your access will still remain active till the
              current billing cycle ends.
            </SectionSub>

            <PrimaryButton onClick={() => navigate("/pricing")}>
              Open Pricing
            </PrimaryButton>

            {manageableSubscription ? (
              <MutedState>
                <RefreshCw size={16} />
                Active paid subscription found. Open that subscription card and
                use its dedicated stop-recurring action.
              </MutedState>
            ) : (
              <MutedState>
                <Lock size={16} />
                No manageable paid subscription found for this account right now.
              </MutedState>
            )}
          </Section>
        </ContentGrid>
      </Shell>

      {showCancelModal && (
        <Overlay>
          <Modal>
            <ModalTitle>Stop recurring payment?</ModalTitle>
            <ModalText>
              {selectedSubscription?.title
                ? `${selectedSubscription.title} will remain active until the end of the current billing cycle. Only the next recurring charge will be stopped.`
                : "Your current plan will remain active until the end of the current billing cycle. Only the next recurring charge will be stopped."}
            </ModalText>

            <ModalActions>
              <SecondaryButton onClick={() => setShowCancelModal(false)}>
                Keep Subscription
              </SecondaryButton>
              <DangerButton onClick={handleCancelConfirm} disabled={cancelling}>
                {cancelling ? "Updating..." : "Stop Recurring"}
              </DangerButton>
            </ModalActions>
          </Modal>
        </Overlay>
      )}
    </Page>
  );
}

const Page = styled.div`
  min-height: 100vh;
  padding: 120px 20px;
  background:
    radial-gradient(860px at 12% 0%, rgba(245, 158, 11, 0.12), transparent 58%),
    radial-gradient(900px at 88% 6%, rgba(56, 189, 248, 0.12), transparent 60%),
    linear-gradient(180deg, #06101d 0%, #040814 100%);
`;

const Shell = styled.div`
  max-width: 1100px;
  margin: 0 auto;
`;

const BackButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 14px;
  border-radius: 999px;
  border: 1px solid rgba(148, 163, 184, 0.16);
  background: rgba(15, 23, 42, 0.52);
  color: #e2e8f0;
  cursor: pointer;
`;

const Hero = styled.div`
  margin-top: 18px;
  display: grid;
  grid-template-columns: minmax(0, 1.5fr) minmax(280px, 0.95fr);
  gap: 22px;

  @media (max-width: 920px) {
    grid-template-columns: 1fr;
  }
`;

const Eyebrow = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  border-radius: 999px;
  background: rgba(56, 189, 248, 0.1);
  border: 1px solid rgba(56, 189, 248, 0.18);
  color: #7dd3fc;
  font-size: 12px;
  font-weight: 900;
  letter-spacing: 0.04em;
  text-transform: uppercase;
`;

const Title = styled.h1`
  margin: 16px 0 0;
  font-size: 42px;
  font-weight: 1000;
  line-height: 1.08;
  color: #f8fafc;
`;

const Sub = styled.p`
  margin-top: 12px;
  font-size: 18px;
  color: #94a3b8;
  line-height: 1.8;
  max-width: 760px;
`;

const SummaryPanel = styled.div`
  padding: 24px;
  border-radius: 28px;
  background:
    linear-gradient(135deg, rgba(245, 158, 11, 0.12), rgba(56, 189, 248, 0.08)),
    rgba(15, 23, 42, 0.78);
  border: 1px solid rgba(245, 158, 11, 0.18);
`;

const SummaryLabel = styled.div`
  color: #fbbf24;
  font-size: 11px;
  font-weight: 900;
  letter-spacing: 0.08em;
  text-transform: uppercase;
`;

const SummaryTitle = styled.div`
  margin-top: 10px;
  color: #f8fafc;
  font-size: 24px;
  line-height: 1.2;
  font-weight: 1000;
`;

const SummaryList = styled.div`
  margin-top: 18px;
  display: grid;
  gap: 10px;
`;

const SummaryItem = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  border-radius: 16px;
  background: rgba(2, 6, 23, 0.48);
  border: 1px solid rgba(148, 163, 184, 0.12);
  color: #cbd5e1;
  font-size: 13px;
  font-weight: 700;
`;

const StatsGrid = styled.div`
  margin-top: 24px;
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 14px;

  @media (max-width: 920px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  @media (max-width: 560px) {
    grid-template-columns: 1fr;
  }
`;

const StatCard = styled.div`
  padding: 18px;
  border-radius: 22px;
  background: rgba(15, 23, 42, 0.62);
  border: 1px solid rgba(148, 163, 184, 0.12);
`;

const StatLabel = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: #94a3b8;
  font-size: 12px;
  font-weight: 800;
`;

const StatValue = styled.div`
  margin-top: 10px;
  color: #f8fafc;
  font-size: 20px;
  font-weight: 1000;
  line-height: 1.4;
`;

const StatHint = styled.div`
  margin-top: 8px;
  color: #64748b;
  font-size: 12px;
  line-height: 1.5;
`;

const ContentGrid = styled.div`
  margin-top: 24px;
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(320px, 0.82fr);
  gap: 20px;

  @media (max-width: 920px) {
    grid-template-columns: 1fr;
  }
`;

const Section = styled.div`
  padding: 24px;
  border-radius: 24px;
  background: rgba(15, 23, 42, 0.62);
  border: 1px solid rgba(148, 163, 184, 0.12);
`;

const SectionTitle = styled.div`
  color: #f8fafc;
  font-size: 18px;
  font-weight: 900;
`;

const SectionSub = styled.p`
  margin: 8px 0 0;
  color: #94a3b8;
  font-size: 14px;
  line-height: 1.7;
`;

const SubscriptionList = styled.div`
  margin-top: 18px;
  display: grid;
  gap: 14px;
`;

const SubscriptionCard = styled.div`
  padding: 18px;
  border-radius: 20px;
  background: linear-gradient(
      135deg,
      rgba(15, 23, 42, 0.82),
      rgba(2, 6, 23, 0.72)
    ),
    rgba(15, 23, 42, 0.7);
  border: 1px solid rgba(148, 163, 184, 0.12);
`;

const SubscriptionTop = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
`;

const SubscriptionTitle = styled.div`
  color: #f8fafc;
  font-size: 18px;
  font-weight: 900;
  line-height: 1.3;
`;

const SubscriptionMeta = styled.div`
  margin-top: 6px;
  color: #94a3b8;
  font-size: 13px;
  font-weight: 700;
`;

const StatusBadge = styled.div`
  flex-shrink: 0;
  padding: 7px 10px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 900;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: ${({ $status }) =>
    $status === "active"
      ? "#bbf7d0"
      : $status === "cancel_requested"
      ? "#fde68a"
      : $status === "cancelled" || $status === "expired"
      ? "#fecaca"
      : "#cbd5e1"};
  background: ${({ $status }) =>
    $status === "active"
      ? "rgba(34, 197, 94, 0.14)"
      : $status === "cancel_requested"
      ? "rgba(245, 158, 11, 0.14)"
      : $status === "cancelled" || $status === "expired"
      ? "rgba(239, 68, 68, 0.14)"
      : "rgba(148, 163, 184, 0.14)"};
  border: 1px solid
    ${({ $status }) =>
      $status === "active"
        ? "rgba(34, 197, 94, 0.22)"
        : $status === "cancel_requested"
        ? "rgba(245, 158, 11, 0.22)"
        : $status === "cancelled" || $status === "expired"
        ? "rgba(239, 68, 68, 0.22)"
        : "rgba(148, 163, 184, 0.18)"};
`;

const Rows = styled.div`
  margin-top: 18px;
`;

const CardActions = styled.div`
  margin-top: 16px;
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
`;

const InlineButton = styled.button`
  min-height: 40px;
  padding: 10px 14px;
  border-radius: 14px;
  border: 1px solid rgba(148, 163, 184, 0.14);
  background: rgba(15, 23, 42, 0.78);
  color: #e2e8f0;
  font-size: 13px;
  font-weight: 800;
  cursor: pointer;
`;

const InlineDangerButton = styled.button`
  min-height: 40px;
  padding: 10px 14px;
  border-radius: 14px;
  border: 1px solid rgba(248, 113, 113, 0.16);
  background: rgba(127, 29, 29, 0.18);
  color: #fecaca;
  font-size: 13px;
  font-weight: 800;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 8px;
`;

const Row = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 12px;
  padding: 12px 0;
  border-bottom: 1px solid rgba(148, 163, 184, 0.1);
`;

const Label = styled.span`
  font-weight: 700;
  font-size: 14px;
  color: #cbd5e1;
`;

const Value = styled.span`
  font-size: 14px;
  color: #f8fafc;
  text-align: right;
`;

const BaseButton = styled.button`
  height: 48px;
  margin-top: 16px;
  border-radius: 14px;
  border: none;
  font-weight: 900;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
`;

const PrimaryButton = styled(BaseButton)`
  width: 100%;
  background: linear-gradient(90deg, #f59e0b, #d97706);
  color: white;
`;

const DangerButton = styled(BaseButton)`
  width: 100%;
  background: #fee2e2;
  color: #b91c1c;
`;

const SecondaryButton = styled(BaseButton)`
  flex: 1;
  background: rgba(15, 23, 42, 0.78);
  border: 1px solid rgba(148, 163, 184, 0.12);
  color: #e2e8f0;
`;

const MutedState = styled.div`
  margin-top: 16px;
  padding: 14px 16px;
  border-radius: 16px;
  background: rgba(56, 189, 248, 0.08);
  border: 1px solid rgba(56, 189, 248, 0.14);
  color: #bae6fd;
  font-size: 13px;
  line-height: 1.7;
  display: flex;
  gap: 10px;
  align-items: center;
`;

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 999;
`;

const Modal = styled.div`
  width: 100%;
  max-width: 420px;
  background: rgba(15, 23, 42, 0.96);
  border: 1px solid rgba(148, 163, 184, 0.16);
  border-radius: 20px;
  padding: 28px;
  box-shadow: 0 30px 80px rgba(0, 0, 0, 0.42);
`;

const ModalTitle = styled.h3`
  font-size: 20px;
  font-weight: 900;
  color: #f8fafc;
`;

const ModalText = styled.p`
  margin-top: 10px;
  font-size: 14px;
  color: #cbd5e1;
  line-height: 1.6;
`;

const ModalActions = styled.div`
  display: flex;
  gap: 12px;
  margin-top: 24px;
`;

const LoadingCard = styled.div`
  height: 220px;
  border-radius: 28px;
  background: linear-gradient(
    90deg,
    rgba(255, 255, 255, 0.05) 20%,
    rgba(255, 255, 255, 0.12) 37%,
    rgba(255, 255, 255, 0.05) 63%
  );
  background-size: 400% 100%;
  animation: shimmer 1.4s ease infinite;

  @keyframes shimmer {
    0% {
      background-position: 100% 0;
    }
    100% {
      background-position: -100% 0;
    }
  }
`;

const LoadingGrid = styled.div`
  margin-top: 20px;
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 20px;
`;

const LoadingBlock = styled(LoadingCard)`
  height: 320px;
`;
